import { useEffect, useMemo, useState } from 'react'
import { ExchangeActivity, Swap, TransactionActivity } from '../interfaces/transactionActivity'
import useWallet from './useWallet'
import LedgerScannerClient from '../clients/LedgerScannerClient'
import useConfig from './useConfig'
import { useActivitySettings, useUserConverts } from '../state'
import { logger } from '../logger'
import { getTimestamp } from '../utils/timestampFormat'
import useDebouncedState from './useDebouncedState'
import OdinClient from '../clients/OdinClient'

export interface HistoryItem {
  kind: 'transaction' | 'exchange' | 'swap'
  item: TransactionActivity | ExchangeActivity | Swap
}

const mapTxs = (
  txs: (TransactionActivity | Swap | ExchangeActivity)[],
  kind: HistoryItem['kind']
) => {
  return txs.map((item) => ({ kind, item }))
}

const useWalletTransactionHistory = () => {
  const [transactions, setTransactions] = useState<TransactionActivity[]>([])
  const [swaps, setSwaps] = useState<Swap[]>([])
  const [swapsLoaded, setSwapsLoaded] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [transactionsFetching, setTransactionsFethcing] = useState<boolean>(false)

  const [debouncedSearchToken, setSearchToken, searchToken] = useDebouncedState('', 300)

  const { tokens } = useConfig()
  const { wallet } = useWallet()

  const { activityTypes } = useActivitySettings()
  const { savedConverts } = useUserConverts()

  const walletConverts = savedConverts[wallet.principalId] || []

  useEffect(() => {
    fetchTransactions(debouncedSearchToken)
  }, [debouncedSearchToken])

  const fetchTransactions = async (searchString: string) => {
    setTransactionsFethcing(true)

    if (searchString.length > 1) {
      const normalizedSearchString = searchString.toLowerCase()

      const token = tokens.find(
        (t) =>
          t.symbol.toLowerCase().startsWith(normalizedSearchString) ||
          t.name.toLowerCase().startsWith(normalizedSearchString)
      )

      if (token) {
        LedgerScannerClient.fetchTransactions(wallet, {
          types: activityTypes,
          pageSize: 100,
          tokenSymbol: token.symbol,
        })
          .then(setTransactions)
          .catch(logger.debug)
          .finally(() => setTransactionsFethcing(false))
      } else {
        setTransactions([])
      }
    } else {
      LedgerScannerClient.fetchTransactions(wallet, {
        types: activityTypes,
        pageSize: 100,
      })
        .then(setTransactions)
        .catch(logger.debug)
        .finally(() => setTransactionsFethcing(false))
    }
  }

  useEffect(() => {
    fetchTransactions(searchToken)
  }, [wallet.principalId, wallet.accountId, JSON.stringify(activityTypes)])

  const handleTabChange = async (newValue: number) => {
    setActiveTab(newValue)

    if (newValue === 1 && !swapsLoaded) {
      try {
        const swaps = await LedgerScannerClient.fetchSwaps(
          wallet.principalId,
          wallet.accountId,
          1,
          100
        )

        setSwaps(swaps)
      } catch (e) {
        logger.error('Failed to load swaps', { originalError: e })
      }
    }
  }

  const refreshTransactions = () => {
    fetchTransactions(debouncedSearchToken)
  }

  const historyItems: HistoryItem[] = useMemo(() => {
    const txs: HistoryItem[] = mapTxs(transactions || [], 'transaction')

    return txs.sort((a, b) => {
      const timestampA = getTimestamp(a.item)
      const timestampB = getTimestamp(b.item)

      return Number(timestampB - timestampA)
    })
  }, [transactions, searchToken, activityTypes])

  return {
    activeTab,
    handleTabChange,
    searchToken,
    setSearchToken,
    swaps: mapTxs(swaps, 'swap'),
    exchanges: mapTxs(walletConverts, 'exchange'),
    historyItems,
    transactionsFetching,
    refreshTransactions,
  }
}

export default useWalletTransactionHistory
